import React from "react";

/**
 * App-wide React Error Boundary.
 * Without this, any unhandled error inside a page unmounts the whole React tree
 * and the user sees a completely blank page (the dreaded "black screen" bug).
 * With it, we render a recoverable fallback that keeps the shell visible.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ error: null });
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen bg-[#0A0A0A] text-zinc-200 flex items-center justify-center p-6"
      >
        <div className="max-w-md w-full border border-[#262626] bg-[#0E0E0E] p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-red-400 mb-2">
            Something broke
          </div>
          <div className="text-lg font-medium mb-3">This page failed to render.</div>
          <div className="text-xs text-zinc-500 leading-relaxed mb-5">
            We caught the error so the rest of Quill keeps working. If this keeps
            happening, try reloading the page or contact support.
          </div>
          <pre className="text-[10px] text-zinc-600 bg-black/40 p-3 mb-5 max-h-40 overflow-auto whitespace-pre-wrap break-all">
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <div className="flex gap-2">
            <button
              data-testid="error-boundary-reload-btn"
              onClick={this.handleReload}
              className="bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-200"
            >
              Reload
            </button>
            <a
              data-testid="error-boundary-home-link"
              href="/"
              className="border border-[#262626] text-zinc-300 text-sm px-4 py-2 rounded-md hover:border-zinc-500"
            >
              Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
